"use client";

import { useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const LibraryOwnerDashboard = ({ dashboardData, slotData, dateRange }) => {
  const [selectedView, setSelectedView] = useState("slots");
  console.log(dashboardData);

  // Process slot data for charts
  const processSlotData = () => {
    const slotLabels = slotData.map(
      (slot) =>
        `${slot.slotId} (${slot.fromTimeHours}${slot.fromTimestamp}-${slot.toTimeHours}${slot.toTimestamp})`
    );
    const bookedSeats = slotData.map((slot) => slot.bookedSeats);
    const totalSeats = slotData.map((slot) => slot.numberOfSeats);
    const revenue = slotData.map(
      (slot) => slot.bookedSeats * slot.libraryPrice
    );

    return { slotLabels, bookedSeats, totalSeats, revenue };
  };

  const { slotLabels, bookedSeats, totalSeats, revenue } = processSlotData();

  // Bar chart data for slot-wise bookings
  const barChartData = {
    labels: slotLabels,
    datasets: [
      {
        label: "Booked Seats",
        data: bookedSeats,
        backgroundColor: "rgba(79, 70, 229, 0.8)",
        borderColor: "rgba(79, 70, 229, 1)",
        borderWidth: 2,
        borderRadius: 6,
      },
      {
        label: "Total Seats",
        data: totalSeats,
        backgroundColor: "rgba(229, 231, 235, 0.8)",
        borderColor: "rgba(156, 163, 175, 1)",
        borderWidth: 2,
        borderRadius: 6,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: "500",
          },
        },
      },
      title: {
        display: true,
        text: "Slot-wise Booking Comparison",
        font: {
          size: 16,
          weight: "600",
        },
        padding: 20,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "#4f46e5",
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          afterLabel: (context) => {
            if (context.datasetIndex === 0) {
              const slotIndex = context.dataIndex;
              const price = slotData[slotIndex]?.libraryPrice || 0;
              const revenue = context.parsed.y * price;
              return `Revenue: ₹${revenue}`;
            }
            return "";
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 10,
          },
          maxRotation: 45,
        },
      },
    },
  };

  // Doughnut chart for occupancy rate
  const occupancyData = {
    labels: ["Booked Seats", "Available Seats"],
    datasets: [
      {
        data: [
          bookedSeats.reduce((a, b) => a + b, 0),
          totalSeats.reduce((a, b) => a + b, 0) -
          bookedSeats.reduce((a, b) => a + b, 0),
        ],
        backgroundColor: ["#4f46e5", "#e5e7eb"],
        borderColor: ["#4f46e5", "#9ca3af"],
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: "500",
          },
        },
      },
      title: {
        display: true,
        text: "Overall Occupancy Rate",
        font: {
          size: 16,
          weight: "600",
        },
        padding: 20,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "#4f46e5",
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          },
        },
      },
    },
    cutout: "60%",
  };

  return (
    <>
      {/* Slot-wise Data Table */}
      <div className="col-lg-12 mb-4">
        <div className="dashboard-table-card">
          <div className="table-header">
            <h6 className="mb-0">
              <i className="fa fa-clock me-2"></i>
              Total Bookings - Slot Wise
            </h6>
            <div className="header-actions">
              <span className="badge bg-primary">
                {slotData.length} Active Slots
              </span>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Slot</th>
                  <th>Time</th>
                  <th>Price (₹)</th>
                  <th>Total Seats</th>
                  <th>Booked</th>
                  <th>Available</th>
                  <th>Revenue (₹)</th>
                  <th>Occupancy</th>
                </tr>
              </thead>
              <tbody>
                {slotData.map((slot, index) => {
                  const occupancyRate = (
                    (slot.bookedSeats / slot.numberOfSeats) *
                    100
                  ).toFixed(1);
                  const slotRevenue = slot.bookedSeats * slot.libraryPrice;

                  return (
                    <tr key={slot._id}>
                      <td>
                        <span className="slot-id-badge">{slot.slot_name}</span>
                      </td>
                      <td>
                        <div className="time-slot">
                          <strong>
                            {slot.fromTimeHours} {slot.fromTimestamp} -{" "}
                            {slot.toTimeHours} {slot.toTimestamp}
                          </strong>
                        </div>
                      </td>
                      <td>
                        <span className="price-tag">₹{slot.libraryPrice}</span>
                      </td>
                      <td>
                        <span className="seat-count total-seats">
                          {slot.numberOfSeats}
                        </span>
                      </td>
                      <td>
                        <span className="seat-count booked-seats">
                          {slot.bookedSeats}
                        </span>
                      </td>
                      <td>
                        <span className="seat-count available-seats">
                          {slot.remainingSeats}
                        </span>
                      </td>
                      <td>
                        <span className="revenue-amount">₹{slotRevenue}</span>
                      </td>
                      <td>
                        <div className="occupancy-indicator">
                          <div className="progress-bar-container">
                            <div
                              className="progress-bar-fill"
                              style={{ width: `${occupancyRate}%` }}
                            ></div>
                          </div>
                          <span className="occupancy-text">
                            {occupancyRate}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="col-lg-8 mb-4">
        <div className="dashboard-chart-card">
          <div className="chart-header">
            <h6 className="mb-0">
              <i className="fa fa-chart-bar me-2"></i>
              Graphical Representation - Booking Analysis
            </h6>
            {/* <div className="chart-controls">
                            <button
                                className={`btn btn-sm ${selectedView === "slots" ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={() => setSelectedView("slots")}
                            >
                                Slot View
                            </button>
                            <button
                                className={`btn btn-sm ${selectedView === "revenue" ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={() => setSelectedView("revenue")}
                            >
                                Revenue View
                            </button>
                        </div> */}
          </div>
          <div className="chart-container">
            <Bar data={barChartData} options={barChartOptions} />
          </div>
        </div>
      </div>

      {/* Summary Cards and Occupancy Chart */}
      <div className="col-lg-4 mb-4">
        <div className="summary-cards-container ">
          {/* Summary Stats */}
          <div className="summary-card revenue-summary bg-white">
            <div className="summary-contentt">
              <div className="summary-icon">
                <i className="fa fa-rupee-sign"></i>
              </div>
              <div className="summary-details">
                {/* <h4>₹{revenue.reduce((a, b) => a + b, 0)}</h4> */}
                <h4>₹ {dashboardData?.currentMonthSummary?.totalAmount || 0}</h4>
                <p>Total Revenue</p>
                <small>Current month</small>
              </div>
            </div>
          </div>

          <div className="summary-card occupancy-summary bg-white">
            <div className="summary-contentt">
              <div className="summary-icon">
                <i className="fa fa-percentage"></i>
              </div>
              <div className="summary-details">
                <h4>
                  {(
                    (bookedSeats.reduce((a, b) => a + b, 0) /
                      totalSeats.reduce((a, b) => a + b, 0)) *
                    100
                  ).toFixed(1)}
                  %
                </h4>
                <p>Avg Occupancy</p>
                <small>Across all slots</small>
              </div>
            </div>
          </div>

          {/* Occupancy Doughnut Chart */}
          <div className="occupancy-chart-card">
            <div className="chart-container-small">
              <Doughnut data={occupancyData} options={doughnutOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Students */}
      <div className="col-lg-12">
        <div className="recent-students-card">
          <div className="students-header">
            <h6 className="mb-0 text-white">
              <i className="fa fa-users me-2"></i>
              Recent Student Registrations
            </h6>
            <span className="badge bg-light text-dark">
              {dashboardData.recentStudents?.length || 0} New
            </span>
          </div>
          <div className="students-content">
            {dashboardData.recentStudents?.length > 0 ? (
              dashboardData.recentStudents.map((student, index) => (
                <div key={index} className="student-item">
                  <div className="student-avatar">
                    <i className="fa fa-user"></i>
                  </div>
                  <div className="student-details">
                    <h6 className="student-name">{student.name}</h6>
                    <p className="student-email">{student.email}</p>
                    <div className="student-meta">
                      <span className={`payment-badge ${student.paymentMode}`}>
                        {student.paymentMode}
                      </span>
                      <span className="join-date">
                        {new Date(student.joinedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <i className="fa fa-users fa-3x text-muted mb-3"></i>
                <p className="text-muted">No recent registrations</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style >{`
        .dashboard-table-card,
        .dashboard-chart-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #f0f0f0;
          overflow: hidden;
        }

        .table-header,
        .chart-header {
          background: #26a084;
          color: white;
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .table-header h6,
        .chart-header h6 {
          font-weight: 600;
          font-size: 20px;
          margin: 0;
          color: #fff !important;
        }
        .table thead th{
            background:#26a084;
        }
        .header-actions .badge {
          background: rgba(255, 255, 255, 0.2) !important;
          color: white;
        }

        .chart-controls {
          display: flex;
          gap: 8px;
        }

        .chart-controls .btn {
          padding: 4px 12px;
          font-size: 12px;
          border-radius: 6px;
        }

        .table {
          font-size: 13px;
          margin: 0;
        }

        .table th {
          background: #f8fafc;
          border-top: none;
          font-weight: 600;
          color: #374151;
          padding: 12px 15px;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .table td {
          padding: 15px;
          vertical-align: middle;
          border-top: 1px solid #f1f5f9;
        }

        .table-hover tbody tr:hover {
          background-color: #f8fafc;
        }

        .slot-id-badge {
          background: #4f46e5;
          color: white;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 600;
        }

        .time-slot {
          font-size: 13px;
          color: #374151;
        }

        .price-tag {
          background: #059669;
          color: white;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
        }

        .seat-count {
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
        }

        .total-seats {
          background: #e5e7eb;
          color: #374151;
        }

        .booked-seats {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .available-seats {
          background: #d1fae5;
          color: #059669;
        }

        .revenue-amount {
          color: #059669;
          font-weight: 600;
        }

        .occupancy-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .progress-bar-container {
          width: 60px;
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #4f46e5, #7c3aed);
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .occupancy-text {
          font-size: 11px;
          font-weight: 600;
          color: #374151;
        }

        .chart-container {
          padding: 30px 20px;
          height: 400px;
        }

        .chart-container-small {
          padding: 20px;
          height: 300px;
        }

        .summary-cards-container {
          display: flex;
          flex-direction: column;
          gap: 15px;
          height: 100%;
        }

        .summary-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #f0f0f0;
        }

        .summary-contentt {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .summary-icon {
          width: 50px;
          height: 50px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          color: white;
        }

        .revenue-summary .summary-icon {
          background: #059669;
        }

        .occupancy-summary .summary-icon {
          background: #dc2626;
        }

        .summary-details h4 {
          font-size: 1.8rem;
          font-weight: 700;
          margin: 0 0 5px 0;
          color: #111827;
        }

        .summary-details p {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 3px 0;
        }

        .summary-details small {
          font-size: 12px;
          color: #6b7280;
        }

        .occupancy-chart-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #f0f0f0;
          flex: 1;
        }

        .recent-students-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #f0f0f0;
          overflow: hidden;
        }

        .students-header {
          background: #26a084;
          color: white;
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .students-header h6 {
          font-size: 20px;
        }
        .students-content {
          padding: 20px;
          max-height: 300px;
          overflow-y: auto;
        }

        .student-item {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 15px 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .student-item:last-child {
          border-bottom: none;
        }

        .student-avatar {
          width: 45px;
          height: 45px;
          border-radius: 50%;
          background: #4f46e5;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 16px;
        }

        .student-details {
          flex: 1;
        }

        .student-name {
          font-size: 14px;
          font-weight: 600;
          color: #111827;
          margin: 0 0 5px 0;
        }

        .student-email {
          font-size: 12px;
          color: #6b7280;
          margin: 0 0 8px 0;
        }

        .student-meta {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .payment-badge {
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .payment-badge.online {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .payment-badge.offline {
          background: #fef3c7;
          color: #d97706;
        }

        .join-date {
          font-size: 11px;
          color: #6b7280;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
        }

        @media (max-width: 768px) {
          .table-responsive {
            font-size: 11px;
          }

          .chart-container {
            height: 300px;
            padding: 20px 15px;
          }

          .summary-cards-container {
            gap: 10px;
          }

          .summary-card {
            padding: 15px;
          }

          .summary-contentt {
            flex-direction: column;
            text-align: center;
            gap: 10px;
          }

          .student-item {
            flex-direction: column;
            text-align: center;
            gap: 10px;
          }
        }
      `}</style>
    </>
  );
};

export default LibraryOwnerDashboard;
